'use client'

import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'

import { loadGoogleTagManager } from '@/lib/analytics/googleTagManager'
import { onAnalyticsLoadRequested } from '@/lib/analytics/requestAnalyticsLoad'
import { hasTrackingConsent } from '@/lib/analytics/trackingConsent'
import { scheduleNonCriticalWork } from '@/lib/perf/scheduleNonCriticalWork'

// Meta Pixel. Unset in an environment => the pixel is simply never rendered,
// so a missing env var degrades to today's behaviour instead of shipping a
// broken `fbq('init', 'undefined')`.
const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID

const FB_PIXEL_BOOTSTRAP = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${FB_PIXEL_ID}');
fbq('track', 'PageView');`

// TikTok Pixel, gated on consent and env the same way as the Meta Pixel.
const TIKTOK_PIXEL_ID = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID

const TIKTOK_PIXEL_BOOTSTRAP = `!function (w, d, t) {
w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
ttq.load('${TIKTOK_PIXEL_ID}');
ttq.page();
}(window, document, 'ttq');`

const DeferredAnalyticsScripts = () => {
  const pathname = usePathname()
  const [shouldLoad, setShouldLoad] = useState(false)
  // The bootstrap snippet fires the first PageView itself; this ref skips the
  // render that mounts it so the landing view is not counted twice.
  const pixelPageViewSent = useRef(false)

  useEffect(() => {
    const activate = () => {
      loadGoogleTagManager()
      setShouldLoad(true)
    }

    if (hasTrackingConsent()) {
      // Deliberately not schedulePathAwareNonCriticalWork: on the home path that
      // helper waits for user intent with no deadline, so a visitor who lands
      // and reads without scrolling is never counted — the landing PageView, the
      // one Meta is most often asked for, simply never fires.
      // scheduleNonCriticalWork still activates on those same intent events; it
      // just also gives up waiting after an idle deadline.
      return scheduleNonCriticalWork(activate)
    }

    // Consent granted on this page: load at once. Waiting for idle here cost the
    // /join landing page_view — Safari has no requestIdleCallback, so the
    // fallback timeout alone held GTM back for 3.5s after "Accept all".
    return onAnalyticsLoadRequested(activate)
  }, [pathname])

  // Client-side navigations do not re-run the bootstrap, so the SPA has to
  // report each subsequent route itself. Meta only: the TikTok SDK watches the
  // History API on its own (HistoryObserver), a manual ttq.page() would double
  // count.
  useEffect(() => {
    if (!shouldLoad || !FB_PIXEL_ID) {
      return
    }

    if (!pixelPageViewSent.current) {
      pixelPageViewSent.current = true
      return
    }

    window.fbq?.('track', 'PageView')
  }, [shouldLoad, pathname])

  if (!shouldLoad) {
    return null
  }

  return (
    <>
      {FB_PIXEL_ID ? (
        <Script id="fb-pixel" strategy="afterInteractive">
          {FB_PIXEL_BOOTSTRAP}
        </Script>
      ) : null}
      {TIKTOK_PIXEL_ID ? (
        <Script id="tiktok-pixel" strategy="afterInteractive">
          {TIKTOK_PIXEL_BOOTSTRAP}
        </Script>
      ) : null}
    </>
  )
}

export default DeferredAnalyticsScripts
